import { useState } from "react";
import { Eye, EyeOff, Trash2, Folder, FolderOpen, ChevronRight, ChevronDown, GripVertical, FolderPlus } from "lucide-react";

const uidF = () => "f_" + Math.random().toString(36).slice(2, 9);

// Move part `dragId` before `beforeId` (or to the end) inside `folderId`
const movePart = (parts, dragId, folderId, beforeId) => {
  const item = parts.find((p) => p.id === dragId); if (!item) return parts;
  const rest = parts.filter((p) => p.id !== dragId);
  const moved = { ...item, folderId: folderId || null };
  let idx = beforeId ? rest.findIndex((p) => p.id === beforeId) : -1;
  if (idx < 0) {
    const lastInGroup = rest.map((p) => (p.folderId || null) === (folderId || null)).lastIndexOf(true);
    idx = lastInGroup >= 0 ? lastInGroup + 1 : rest.length;
  }
  rest.splice(idx, 0, moved);
  return rest;
};

const VisBtn = ({ on, onClick, testid, title }) => (
  <button onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testid} title={title} className="text-kann-text-sec hover:text-kann-text-pri">
    {on ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
  </button>
);

export const PartsTree = ({ parts, folders, setParts, setFolders, selPartId, setSelPartId, isVisible, setVisible, removePart, setPartField }) => {
  const [drag, setDrag] = useState(null); // {type:'part'|'folder', id}
  const [over, setOver] = useState(null);

  const onDragStart = (type, id) => (e) => { e.stopPropagation(); setDrag({ type, id }); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); };
  const onDragEnd = () => { setDrag(null); setOver(null); };
  const allow = (key) => (e) => { if (!drag) return; e.preventDefault(); e.stopPropagation(); if (over !== key) setOver(key); };

  const dropOnPart = (target) => (e) => {
    e.preventDefault(); e.stopPropagation();
    if (drag?.type === "part" && drag.id !== target.id) setParts((prev) => movePart(prev, drag.id, target.folderId, target.id));
    onDragEnd();
  };
  const dropOnFolder = (fid) => (e) => {
    e.preventDefault(); e.stopPropagation();
    if (drag?.type === "part") {
      setParts((prev) => movePart(prev, drag.id, fid, null));
      setFolders((prev) => prev.map((f) => (f.id === fid ? { ...f, collapsed: false } : f)));
    } else if (drag?.type === "folder" && drag.id !== fid) {
      setFolders((prev) => { const item = prev.find((f) => f.id === drag.id); const rest = prev.filter((f) => f.id !== drag.id); rest.splice(rest.findIndex((f) => f.id === fid), 0, item); return rest; });
    }
    onDragEnd();
  };
  const dropOnRoot = (e) => { e.preventDefault(); if (drag?.type === "part") setParts((prev) => movePart(prev, drag.id, null, null)); onDragEnd(); };

  const addFolder = () => setFolders((prev) => [...prev, { id: uidF(), name: `Map ${prev.length + 1}`, collapsed: false }]);
  const removeFolder = (fid) => { setParts((prev) => prev.map((p) => (p.folderId === fid ? { ...p, folderId: null } : p))); setFolders((prev) => prev.filter((f) => f.id !== fid)); };
  const patchFolder = (fid, patch) => setFolders((prev) => prev.map((f) => (f.id === fid ? { ...f, ...patch } : f)));

  const partRow = (p, indent) => (
    <div key={p.id} draggable onDragStart={onDragStart("part", p.id)} onDragEnd={onDragEnd} onDragOver={allow(p.id)} onDrop={dropOnPart(p)}
      onClick={() => setSelPartId(p.id)} data-testid={`is-part-${p.id}`}
      className={`group rounded-md pr-2 py-1.5 cursor-pointer flex items-center gap-1.5 transition-colors ${indent ? "pl-5" : "pl-0.5"} ${over === p.id && drag?.id !== p.id ? "border-t-2 border-kann-gold" : "border-t-2 border-transparent"} ${drag?.id === p.id ? "opacity-40" : ""} ${selPartId === p.id ? "bg-kann-gold/15 ring-1 ring-kann-gold" : "hover:bg-kann-surface-active"}`}>
      <GripVertical className="w-3.5 h-3.5 shrink-0 text-kann-text-sec/50 group-hover:text-kann-text-sec cursor-grab" data-testid={`is-part-drag-${p.id}`} />
      <span className="w-3 h-3 rounded-sm shrink-0 border border-kann-border/40" style={{ background: p.color }} />
      <input value={p.name} onChange={(e) => setPartField(p.id, { name: e.target.value })} onClick={(e) => { e.stopPropagation(); setSelPartId(p.id); }} className={`flex-1 min-w-0 bg-transparent text-xs outline-none ${isVisible(p.id) ? "text-kann-text-pri" : "text-kann-text-sec line-through"}`} />
      <VisBtn on={isVisible(p.id)} onClick={() => setVisible([p.id], !isVisible(p.id))} testid={`is-part-vis-${p.id}`} title="Toon/verberg onderdeel" />
      <button onClick={(e) => { e.stopPropagation(); removePart(p.id); }} data-testid={`is-part-del-${p.id}`} className="text-kann-text-sec hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );

  const folderIds = new Set(folders.map((f) => f.id));
  const rootParts = parts.filter((p) => !p.folderId || !folderIds.has(p.folderId));

  return (
    <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="is-parts-list" onDragOver={allow("root")} onDrop={dropOnRoot}>
      <button onClick={addFolder} data-testid="is-add-folder-btn" className="w-full mb-1 text-[11px] text-kann-text-sec hover:text-kann-text-pri hover:bg-kann-surface-active rounded-md px-2 py-1 flex items-center gap-1.5"><FolderPlus className="w-3.5 h-3.5" /> Nieuwe map</button>
      {parts.length === 0 && <div className="text-xs text-kann-text-sec p-3">Nog geen onderdelen.</div>}
      {folders.map((f) => {
        const kids = parts.filter((p) => p.folderId === f.id);
        const anyVis = kids.some((p) => isVisible(p.id));
        return (
          <div key={f.id} data-testid={`is-folder-${f.id}`} onDragOver={allow(f.id)} onDrop={dropOnFolder(f.id)} className={drag?.id === f.id ? "opacity-40" : ""}>
            <div draggable onDragStart={onDragStart("folder", f.id)} onDragEnd={onDragEnd} onDragOver={allow(f.id)} onDrop={dropOnFolder(f.id)}
              className={`group rounded-md pr-2 py-1.5 flex items-center gap-1 cursor-pointer transition-colors ${over === f.id ? "bg-kann-gold/20 ring-1 ring-kann-gold" : "hover:bg-kann-surface-active"}`}
              onClick={() => patchFolder(f.id, { collapsed: !f.collapsed })}>
              <GripVertical className="w-3.5 h-3.5 shrink-0 text-kann-text-sec/50 group-hover:text-kann-text-sec cursor-grab" />
              {f.collapsed ? <ChevronRight className="w-3.5 h-3.5 text-kann-text-sec" /> : <ChevronDown className="w-3.5 h-3.5 text-kann-text-sec" />}
              {f.collapsed ? <Folder className="w-3.5 h-3.5 text-kann-gold shrink-0" /> : <FolderOpen className="w-3.5 h-3.5 text-kann-gold shrink-0" />}
              <input value={f.name} onChange={(e) => patchFolder(f.id, { name: e.target.value })} onClick={(e) => e.stopPropagation()} data-testid={`is-folder-name-${f.id}`} className={`flex-1 min-w-0 bg-transparent text-xs font-semibold outline-none ${anyVis || !kids.length ? "text-kann-text-pri" : "text-kann-text-sec line-through"}`} />
              <span className="text-[10px] text-kann-text-sec tabular-nums">{kids.length}</span>
              <VisBtn on={anyVis || !kids.length} onClick={() => kids.length && setVisible(kids.map((k) => k.id), !anyVis)} testid={`is-folder-vis-${f.id}`} title="Toon/verberg hele map" />
              <button onClick={(e) => { e.stopPropagation(); removeFolder(f.id); }} data-testid={`is-folder-del-${f.id}`} title="Map verwijderen (onderdelen blijven)" className="text-kann-text-sec hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            {!f.collapsed && (
              <div className="space-y-1 mt-1">
                {kids.map((p) => partRow(p, true))}
                {!kids.length && <div className="pl-7 py-1 text-[11px] text-kann-text-sec italic">Sleep onderdelen hierheen</div>}
              </div>
            )}
          </div>
        );
      })}
      {rootParts.map((p) => partRow(p, false))}
      {drag?.type === "part" && <div className={`rounded-md border border-dashed py-2 text-center text-[11px] ${over === "root" ? "border-kann-gold text-kann-gold" : "border-kann-border/50 text-kann-text-sec"}`}>Hier neerzetten (buiten map)</div>}
    </div>
  );
};
